<template>
  <a-drawer
    :visible="visible"
    :title="drawerTitle"
    :width="720"
    placement="right"
    :destroy-on-close="true"
    @close="handleClose"
  >
    <a-form
      :model="formData"
      layout="vertical"
      ref="formRef"
    >
      <!-- 채널 기본 정보 -->
      <a-divider orientation="left">채널 기본 정보</a-divider>

      <a-form-item
        label="채널 ID"
        name="channelId"
        :rules="[{ required: true, message: '채널 ID를 입력하세요' }]"
      >
        <a-input
          v-model:value="formData.channelId"
          placeholder="@certilife"
          :disabled="mode === 'edit'"
        />
      </a-form-item>

      <a-form-item
        label="채널명"
        name="channelName"
        :rules="[{ required: true, message: '채널명을 입력하세요' }]"
      >
        <a-input
          v-model:value="formData.channelName"
          placeholder="서티라이프 certilife"
        />
      </a-form-item>

      <a-form-item
        label="Sender Key"
        name="senderKey"
        :rules="[{ required: true, message: 'Sender Key를 입력하세요' }]"
      >
        <a-input
          v-model:value="formData.senderKey"
          placeholder="Sender Key를 입력하세요"
        />
      </a-form-item>

      <!-- API 인증 정보 -->
      <a-divider orientation="left">API 인증 정보</a-divider>

      <a-form-item
        label="Client ID"
        name="clientId"
        :rules="[{ required: true, message: 'Client ID를 입력하세요' }]"
      >
        <a-input
          v-model:value="formData.clientId"
          placeholder="R000000095"
        />
      </a-form-item>

      <a-form-item
        label="Client Secret"
        name="clientSecret"
        :rules="[{ required: true, message: 'Client Secret을 입력하세요' }]"
      >
        <a-input-password
          v-model:value="formData.clientSecret"
          placeholder="Client Secret을 입력하세요"
        />
      </a-form-item>

      <!-- 토큰 정보 -->
      <a-divider orientation="left">토큰 정보 (선택)</a-divider>

      <a-form-item
        label="Token"
        name="token"
      >
        <a-input
          v-model:value="formData.token"
          placeholder="Token 값을 입력하세요"
        />
      </a-form-item>

      <a-form-item
        label="Management Token"
        name="managementToken"
      >
        <a-input
          v-model:value="formData.managementToken"
          placeholder="Management Token 값을 입력하세요"
        />
      </a-form-item>
    </a-form>

    <!-- 하단 버튼 -->
    <div class="drawer-footer">
      <a-space>
        <a-button @click="handleClose">취소</a-button>
        <a-button
          type="primary"
          class="yellow-button"
          @click="handleSave"
          :loading="saving"
        >
          저장
        </a-button>
      </a-space>
    </div>
  </a-drawer>
</template>

<script>
import { defineComponent, ref, watch } from 'vue';
import { message } from 'ant-design-vue';

export default defineComponent({
  name: 'ChannelFormDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    channelData: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: 'create', // 'create', 'edit'
      validator: (value) => ['create', 'edit'].includes(value)
    }
  },
  emits: ['update:visible', 'save', 'close'],
  setup(props, { emit }) {
    const formRef = ref(null);
    const saving = ref(false);

    const formData = ref({
      channelId: '',
      channelName: '',
      senderKey: '',
      clientId: '',
      clientSecret: '',
      token: '',
      managementToken: ''
    });

    const drawerTitle = props.mode === 'create' ? '채널 추가' : '채널 수정';

    // 폼 초기화
    const resetForm = () => {
      formData.value = {
        channelId: '',
        channelName: '',
        senderKey: '',
        clientId: '',
        clientSecret: '',
        token: '',
        managementToken: ''
      };
    };

    // 채널 데이터 변경 감지
    watch(() => props.channelData, (newData) => {
      if (newData) {
        formData.value = {
          channelId: newData.channelId || '',
          channelName: newData.channelName || '',
          senderKey: newData.senderKey || '',
          clientId: newData.clientId || '',
          clientSecret: newData.clientSecret || '',
          token: newData.token || '',
          managementToken: newData.managementToken || ''
        };
      } else {
        resetForm();
      }
    }, { immediate: true });

    // 닫기
    const handleClose = () => {
      emit('update:visible', false);
      emit('close');
      resetForm();
    };

    // 저장
    const handleSave = async () => {
      try {
        await formRef.value.validate();

        // 필수 필드 검증
        if (!formData.value.channelId || !formData.value.channelName ||
            !formData.value.senderKey || !formData.value.clientId ||
            !formData.value.clientSecret) {
          message.warning('모든 필수 항목을 입력해주세요.');
          return;
        }

        saving.value = true;

        // 저장할 데이터
        const saveData = {
          channelId: formData.value.channelId,
          channelName: formData.value.channelName,
          senderKey: formData.value.senderKey,
          clientId: formData.value.clientId,
          clientSecret: formData.value.clientSecret
        };

        // 토큰 필드 추가 (값이 있든 없든 항상 포함)
        if (formData.value.token !== undefined && formData.value.token !== null) {
          saveData.token = formData.value.token;
        }
        if (formData.value.managementToken !== undefined && formData.value.managementToken !== null) {
          saveData.managementToken = formData.value.managementToken;
        }

        emit('save', saveData);

      } catch (error) {
        console.error('폼 검증 오류:', error);
      } finally {
        saving.value = false;
      }
    };

    return {
      formRef,
      saving,
      formData,
      drawerTitle,
      handleClose,
      handleSave
    };
  }
});
</script>

<style scoped>
.drawer-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 24px;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
}

.yellow-button {
  background-color: #FEE500;
  border-color: #FEE500;
  color: #000000;
}

.yellow-button:hover {
  background-color: #FFD700;
  border-color: #FFD700;
  color: #000000;
}

:deep(.ant-divider-horizontal.ant-divider-with-text-left::before) {
  width: 5%;
}

:deep(.ant-divider-horizontal.ant-divider-with-text-left::after) {
  width: 95%;
}
</style>
